<template>
  <router-view />
  <bootstrap-breakpoints
    :class="{ 'visually-hidden': devHelpersHidden }"
  ></bootstrap-breakpoints>
</template>
<script>
import { defineComponent, watch } from "vue";
import { doGetRanksInfo } from "@/store/Ranks/RanksInterfaces";
import { useMenuStore } from "@/stores/menu";
import { useUiPreferencesStore } from "@/stores/uiPreferences";
import BootstrapBreakpoints from "./components/dev/BootstrapBreakpoints";

export default defineComponent({
  data() {
    return {
      devHelpersHidden: !(
        process.env.VUE_APP_DEV_TESTING_HELPERS_VISIBLE === "true"
      ),
    };
  },
  computed: {
    uiPreferencesStore() {
      return useUiPreferencesStore();
    },
  },
  async created() {
    // El ancho del sidebar (--sidebar-width) se expone como CSS variable
    // global para que cualquier layout que dependa de él (DashboardLayout,
    // vistas con position:fixed junto al sidebar) se ajuste solo al
    // colapsar/expandir, sin duplicar el valor en cada archivo.
    //
    // Va ANTES del `await fetchMenus()`: `uiPreferencesStore` lee el estado
    // colapsado de localStorage de forma síncrona y el sidebar puede
    // renderizar colapsado en el primer frame. Con `immediate: true` aquí, la
    // clase del body (que baja --sidebar-width a 72px) se aplica en ese mismo
    // frame. Si se registrara después del await, un usuario con el sidebar
    // colapsado guardado vería el layout reservando 250px hasta que resolviera
    // (o fallara) la carga de menús.
    watch(
      () => this.uiPreferencesStore.sidebarCollapsed,
      (collapsed) => {
        document.body.classList.toggle("sidebar-collapsed", collapsed);
      },
      { immediate: true }
    );

    this.$store.dispatch("ranks/" + doGetRanksInfo);

    // Use Pinia store for menu
    const menuStore = useMenuStore();
    await menuStore.fetchMenus();

    this.$store.dispatch("sys/doGetSystemConfig");
  },
  unmounted() {
    this.$store.dispatch("sys/clearConfigListeners");
  },
  components: { BootstrapBreakpoints },
});
</script>
<style lang="scss">
#app {
  min-height: 100vh;
  overflow: hidden;
}

// Registrada como <length> animable: así el valor de --sidebar-width se
// interpola frame a frame (un solo reloj de animación), en vez de que cada
// consumidor (max-width del sidebar, margin-left del contenido, etc.)
// dispare su propia transición por separado — eran dos animaciones
// independientes con el mismo timing "en teoría", pero visualmente se veían
// desincronizadas (jank normal al animar layout en dos elementos distintos
// a la vez). Con esto los consumidores NO deben declarar su propio
// `transition` sobre la propiedad que lee var(--sidebar-width).
@property --sidebar-width {
  syntax: "<length>";
  inherits: true;
  initial-value: 250px;
}

:root {
  --sidebar-width: 250px;
}

body.sidebar-collapsed {
  --sidebar-width: 72px;
}

body {
  transition: --sidebar-width 0.25s ease;
}
</style>
